import React, { useState, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaChevronRight,
  FaFolder,
  FaFolderOpen,
  FaSearch,
} from "react-icons/fa";
import { Checkbox, Input } from "@/components/ui";
import { Tracker } from "@/models/tracker";
import { ResourceGroup, getGroupRefId } from "@/models/resource-group";
import { useResourceGroups } from "@/api/resource-group";
import { SourceIcon } from "@/components/resource-card";
import { getTrackerValue, getTrackerSource } from "@/utils/tracker-helpers";

// ============================================================================
// Types
// ============================================================================

interface TrackerGroupSelectorProps {
  /** All available trackers */
  trackers: Tracker[];
  /** Currently selected tracker IDs */
  selectedIds: string[];
  /** Callback when selection changes */
  onSelectionChange: (selectedIds: string[]) => void;
  /** Optional className */
  className?: string;
  /** Maximum height of the component */
  maxHeight?: string;
}

interface TrackerItemRowProps {
  tracker: Tracker;
  isSelected: boolean;
  onToggle: () => void;
  depth?: number;
}

interface ResourceGroupRowProps {
  group: ResourceGroup;
  trackers: Tracker[];
  allGroups: ResourceGroup[];
  selectedIds: Set<string>;
  onToggleTracker: (trackerId: string) => void;
  onToggleGroup: (trackerIds: string[]) => void;
  depth?: number;
  searchTerm?: string;
}

// ============================================================================
// Helper Components
// ============================================================================

/**
 * Single tracker row with checkbox
 */
const TrackerItemRow: React.FC<TrackerItemRowProps> = ({
  tracker,
  isSelected,
  onToggle,
  depth = 0,
}) => {
  const trackerValue = getTrackerValue(tracker);
  const source = getTrackerSource(tracker);

  return (
    <div
      className={`flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-surface-secondary cursor-pointer transition-colors`}
      style={{ paddingLeft: `${(depth + 1) * 12 + 8}px` }}
      onClick={onToggle}
    >
      <Checkbox
        checked={isSelected}
        onChange={onToggle}
        size="sm"
        onClick={(e) => e.stopPropagation()}
      />
      <SourceIcon source={source} size="sm" />
      <span className="text-sm text-content-primary truncate flex-1">
        {trackerValue?.value || tracker.id}
      </span>
      {tracker.is_import ? (
        <span className="text-xs text-content-tertiary bg-surface-tertiary px-1.5 py-0.5 rounded">
          Import
        </span>
      ) : (
        <span
          className={`text-xs px-1.5 py-0.5 rounded ${
            tracker.status === "active"
              ? "text-green-600 bg-green-50 dark:text-green-400 dark:bg-green-900/20"
              : "text-gray-500 bg-gray-100 dark:text-gray-400 dark:bg-gray-800"
          }`}
        >
          {tracker.status === "active" ? "Actif" : "Inactif"}
        </span>
      )}
    </div>
  );
};

/**
 * Resource group row with checkbox and collapsible children
 */
const ResourceGroupRow: React.FC<ResourceGroupRowProps> = ({
  group,
  trackers,
  allGroups,
  selectedIds,
  onToggleTracker,
  onToggleGroup,
  depth = 0,
  searchTerm = "",
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // Find child groups
  const childGroups = useMemo(() => {
    return allGroups
      .filter((g) => getGroupRefId(g.parent) === group.id)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [allGroups, group.id]);

  // Find trackers directly in this group
  const directTrackers = useMemo(() => {
    return trackers.filter((t) => getGroupRefId(t.group) === group.id);
  }, [trackers, group.id]);

  // Recursively get all tracker IDs in this group and its children
  const getAllTrackerIdsInGroup = useCallback(
    (groupId: string): string[] => {
      const directIds = trackers
        .filter((t) => getGroupRefId(t.group) === groupId)
        .map((t) => t.id);

      const childGroupIds = allGroups
        .filter((g) => getGroupRefId(g.parent) === groupId)
        .flatMap((g) => getAllTrackerIdsInGroup(g.id!));

      return [...directIds, ...childGroupIds];
    },
    [trackers, allGroups]
  );

  const allTrackerIdsInGroup = useMemo(
    () => getAllTrackerIdsInGroup(group.id!),
    [getAllTrackerIdsInGroup, group.id]
  );

  // Calculate selection state
  const selectedInGroup = allTrackerIdsInGroup.filter((id) =>
    selectedIds.has(id)
  );
  const isFullySelected =
    allTrackerIdsInGroup.length > 0 &&
    selectedInGroup.length === allTrackerIdsInGroup.length;
  const isPartiallySelected =
    selectedInGroup.length > 0 &&
    selectedInGroup.length < allTrackerIdsInGroup.length;

  // Filter trackers by search term if provided
  const visibleDirectTrackers = useMemo(() => {
    if (!searchTerm) return directTrackers;
    const term = searchTerm.toLowerCase();
    return directTrackers.filter((t) => {
      const value = getTrackerValue(t);
      return (
        t.id.toLowerCase().includes(term) ||
        (value?.value && value.value.toLowerCase().includes(term))
      );
    });
  }, [directTrackers, searchTerm]);

  // Check if any children match the search
  const hasMatchingContent = useMemo(() => {
    if (!searchTerm) return true;
    if (visibleDirectTrackers.length > 0) return true;
    // Check child groups recursively
    return childGroups.some((g) => {
      const childTrackers = trackers.filter(
        (t) => getGroupRefId(t.group) === g.id
      );
      return childTrackers.some((t) => {
        const value = getTrackerValue(t);
        const term = searchTerm.toLowerCase();
        return (
          t.id.toLowerCase().includes(term) ||
          (value?.value && value.value.toLowerCase().includes(term))
        );
      });
    });
  }, [searchTerm, visibleDirectTrackers, childGroups, trackers]);

  // Don't render if nothing matches the search
  if (searchTerm && !hasMatchingContent) {
    return null;
  }

  const handleToggleGroup = () => {
    onToggleGroup(allTrackerIdsInGroup);
  };

  const totalCount = allTrackerIdsInGroup.length;
  const hasCustomColor = !!group.color;

  return (
    <div className="group/folder">
      {/* Group Header */}
      <div
        className="flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-surface-secondary cursor-pointer transition-colors"
        style={{ paddingLeft: `${depth * 12 + 8}px` }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {/* Expand/Collapse Icon */}
        <motion.span
          animate={{ rotate: isExpanded ? 90 : 0 }}
          transition={{ duration: 0.2 }}
          className="w-4 flex-shrink-0 text-content-tertiary"
        >
          <FaChevronRight size={10} />
        </motion.span>

        {/* Checkbox for group */}
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={isFullySelected}
            indeterminate={isPartiallySelected}
            onChange={handleToggleGroup}
            size="sm"
          />
        </div>

        {/* Folder Icon */}
        <span
          className={`${!hasCustomColor ? "text-content-tertiary" : ""}`}
          style={hasCustomColor ? { color: group.color! } : undefined}
        >
          {isExpanded ? <FaFolderOpen size={14} /> : <FaFolder size={14} />}
        </span>

        {/* Group Name */}
        <span className="flex-1 text-sm font-medium text-content-primary truncate">
          {group.name || "Dossier sans nom"}
        </span>

        {/* Selection count */}
        <span className="text-xs text-content-tertiary">
          {selectedInGroup.length}/{totalCount}
        </span>
      </div>

      {/* Children */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            {/* Child Groups */}
            {childGroups.map((childGroup) => (
              <ResourceGroupRow
                key={childGroup.id}
                group={childGroup}
                trackers={trackers}
                allGroups={allGroups}
                selectedIds={selectedIds}
                onToggleTracker={onToggleTracker}
                onToggleGroup={onToggleGroup}
                depth={depth + 1}
                searchTerm={searchTerm}
              />
            ))}

            {/* Direct Trackers */}
            {visibleDirectTrackers.map((tracker) => (
              <TrackerItemRow
                key={tracker.id}
                tracker={tracker}
                isSelected={selectedIds.has(tracker.id)}
                onToggle={() => onToggleTracker(tracker.id)}
                depth={depth + 1}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

/**
 * TrackerGroupSelector component - allows selecting trackers by groups or individually.
 * Shows trackers organized in their resource groups with checkboxes.
 * Clicking a group checkbox selects/deselects all trackers in that group.
 */
const TrackerGroupSelector: React.FC<TrackerGroupSelectorProps> = ({
  trackers,
  selectedIds,
  onSelectionChange,
  className = "",
  maxHeight = "400px",
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedSources, setExpandedSources] = useState<Set<string>>(
    new Set()
  );

  // Fetch resource groups
  const { data: resourceGroups = [] } = useResourceGroups("tracker");

  // Convert selected IDs to Set for efficient lookup
  const selectedIdsSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  // Get root-level groups (no parent)
  const rootGroups = useMemo(() => {
    return resourceGroups
      .filter((g) => !g.parent || !getGroupRefId(g.parent))
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [resourceGroups]);

  // Get trackers not in any group
  const ungroupedTrackers = useMemo(() => {
    return trackers.filter((t) => !t.group || !getGroupRefId(t.group));
  }, [trackers]);

  // Group ungrouped trackers by source
  const trackersBySource = useMemo(() => {
    const bySource: Record<string, Tracker[]> = {};
    ungroupedTrackers.forEach((tracker) => {
      const source = getTrackerSource(tracker);
      if (!bySource[source]) {
        bySource[source] = [];
      }
      bySource[source].push(tracker);
    });
    return bySource;
  }, [ungroupedTrackers]);

  // Filter trackers by search term
  const filterTrackerBySearch = useCallback(
    (tracker: Tracker) => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      const value = getTrackerValue(tracker);
      return (
        tracker.id.toLowerCase().includes(term) ||
        (value?.value && value.value.toLowerCase().includes(term))
      );
    },
    [searchTerm]
  );

  // Handlers
  const handleToggleTracker = useCallback(
    (trackerId: string) => {
      const newIds = selectedIdsSet.has(trackerId)
        ? selectedIds.filter((id) => id !== trackerId)
        : [...selectedIds, trackerId];
      onSelectionChange(newIds);
    },
    [selectedIds, selectedIdsSet, onSelectionChange]
  );

  const handleToggleGroup = useCallback(
    (trackerIds: string[]) => {
      const allSelected = trackerIds.every((id) => selectedIdsSet.has(id));
      let newIds: string[];
      if (allSelected) {
        // Deselect all
        newIds = selectedIds.filter((id) => !trackerIds.includes(id));
      } else {
        // Select all
        const toAdd = trackerIds.filter((id) => !selectedIdsSet.has(id));
        newIds = [...selectedIds, ...toAdd];
      }
      onSelectionChange(newIds);
    },
    [selectedIds, selectedIdsSet, onSelectionChange]
  );

  const handleToggleSource = useCallback(
    (source: string, sourceTrackerIds: string[]) => {
      const allSelected = sourceTrackerIds.every((id) => selectedIdsSet.has(id));
      let newIds: string[];
      if (allSelected) {
        newIds = selectedIds.filter((id) => !sourceTrackerIds.includes(id));
      } else {
        const toAdd = sourceTrackerIds.filter((id) => !selectedIdsSet.has(id));
        newIds = [...selectedIds, ...toAdd];
      }
      onSelectionChange(newIds);
    },
    [selectedIds, selectedIdsSet, onSelectionChange]
  );

  const toggleSourceExpanded = useCallback((source: string) => {
    setExpandedSources((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(source)) {
        newSet.delete(source);
      } else {
        newSet.add(source);
      }
      return newSet;
    });
  }, []);

  // Initialize expanded sources
  React.useEffect(() => {
    setExpandedSources(new Set(Object.keys(trackersBySource)));
  }, [trackersBySource]);

  return (
    <div className={`flex flex-col ${className}`}>
      {/* Search */}
      <div className="mb-3">
        <Input
          placeholder="Rechercher un tracker..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          leftIcon={<FaSearch className="text-content-tertiary" />}
          size="sm"
        />
      </div>

      {/* Trackers list */}
      <div
        className="overflow-y-auto border border-arc-border rounded-arc-md"
        style={{ maxHeight }}
      >
        {/* Resource Groups */}
        {rootGroups.length > 0 && (
          <div className="p-2">
            <div className="text-xs font-semibold text-content-secondary uppercase tracking-wider px-2 py-1 mb-1">
              Dossiers
            </div>
            {rootGroups.map((group) => (
              <ResourceGroupRow
                key={group.id}
                group={group}
                trackers={trackers}
                allGroups={resourceGroups}
                selectedIds={selectedIdsSet}
                onToggleTracker={handleToggleTracker}
                onToggleGroup={handleToggleGroup}
                searchTerm={searchTerm}
              />
            ))}
          </div>
        )}

        {/* Ungrouped trackers by source */}
        {Object.keys(trackersBySource).length > 0 && (
          <div className="p-2 border-t border-arc-border">
            <div className="text-xs font-semibold text-content-secondary uppercase tracking-wider px-2 py-1 mb-1">
              Trackers par source
            </div>
            {Object.entries(trackersBySource).map(([source, sourceTrackers]) => {
              const visibleTrackers = sourceTrackers.filter(filterTrackerBySearch);
              if (searchTerm && visibleTrackers.length === 0) return null;

              const trackerIds = sourceTrackers.map((t) => t.id);
              const selectedInSource = trackerIds.filter((id) =>
                selectedIdsSet.has(id)
              );
              const isFullySelected =
                trackerIds.length > 0 &&
                selectedInSource.length === trackerIds.length;
              const isPartiallySelected =
                selectedInSource.length > 0 &&
                selectedInSource.length < trackerIds.length;
              const isExpanded = expandedSources.has(source);

              return (
                <div key={source} className="group/source">
                  {/* Source Header */}
                  <div
                    className="flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-surface-secondary cursor-pointer transition-colors"
                    onClick={() => toggleSourceExpanded(source)}
                  >
                    {/* Expand/Collapse */}
                    <motion.span
                      animate={{ rotate: isExpanded ? 90 : 0 }}
                      transition={{ duration: 0.2 }}
                      className="w-4 flex-shrink-0 text-content-tertiary"
                    >
                      <FaChevronRight size={10} />
                    </motion.span>

                    {/* Checkbox */}
                    <div onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={isFullySelected}
                        indeterminate={isPartiallySelected}
                        onChange={() => handleToggleSource(source, trackerIds)}
                        size="sm"
                      />
                    </div>

                    {/* Source Icon */}
                    <SourceIcon source={source} size="sm" />

                    {/* Source Name */}
                    <span className="flex-1 text-sm font-medium text-content-primary truncate">
                      {source}
                    </span>

                    {/* Count */}
                    <span className="text-xs text-content-tertiary">
                      {selectedInSource.length}/{trackerIds.length}
                    </span>
                  </div>

                  {/* Trackers */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        {visibleTrackers.map((tracker) => (
                          <TrackerItemRow
                            key={tracker.id}
                            tracker={tracker}
                            isSelected={selectedIdsSet.has(tracker.id)}
                            onToggle={() => handleToggleTracker(tracker.id)}
                            depth={1}
                          />
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        )}

        {/* Empty state */}
        {trackers.length === 0 && (
          <div className="p-8 text-center text-content-secondary">
            Aucun tracker disponible
          </div>
        )}

        {/* No results */}
        {trackers.length > 0 &&
          searchTerm &&
          rootGroups.length === 0 &&
          Object.values(trackersBySource).every(
            (s) => s.filter(filterTrackerBySearch).length === 0
          ) && (
            <div className="p-8 text-center text-content-secondary">
              Aucun résultat pour "{searchTerm}"
            </div>
          )}
      </div>
    </div>
  );
};

TrackerGroupSelector.displayName = "TrackerGroupSelector";

export default TrackerGroupSelector;
